import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { Commission } from 'types';
import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { addParamsToUrl } from 'utils/query';

import { eventApi, tagTypes as eventTagTypes } from '../event';

import {
  Analytics,
  AnalyticsSales,
  AnalyticsProduct,
  AnalyticsEmployee,
  AnalyticsLocation,
  GetAnalyticsRequest,
  EditTransactionRequest,
  AnalyticsPaymentMethod,
  AnalyticsEmployeeDetails,
  GetAnalyticsEmployeeRequest,
} from './types';

const tagTypes = ['analytics'];

export const analyticsApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'analyticsApi',
  endpoints: (build) => ({
    get: build.query<Analytics, GetAnalyticsRequest>({
      providesTags: tagTypes,
      query: (query) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_ANALYTICS, query),
      }),
    }),

    employee: build.query<
      AnalyticsEmployeeDetails,
      GetAnalyticsEmployeeRequest
    >({
      keepUnusedDataFor: 1,
      providesTags: tagTypes,
      query: ({ id, ...query }) => ({
        method: 'get',
        url: addParamsToUrl(
          URL.GET_ANALYTICS_EMPLOYEE.replace(':id', String(id)),
          query
        ),
      }),
    }),

    editTransaction: build.mutation<Commission[], EditTransactionRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.GET_ANALYTICS_CHECKOUT_EDIT, { id: String(id) }),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(eventApi.util.invalidateTags(eventTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const { useGetQuery, useEmployeeQuery, useEditTransactionMutation } =
  analyticsApi;
export type {
  AnalyticsSales,
  AnalyticsProduct,
  AnalyticsEmployee,
  AnalyticsLocation,
  AnalyticsPaymentMethod,
  AnalyticsEmployeeDetails,
};

export { tagTypes };
